/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1671667200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Dec 22 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "braze-cohorts",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Get Braze data import key",
          "depth": 1,
          "id": "get-braze-data-import-key"
        }, {
          "value": "Add Braze Cohorts as a destination in Hightouch",
          "depth": 1,
          "id": "add-braze-cohorts-as-a-destination-in-hightouch"
        }, {
          "value": "Sync a model or audience into Braze Cohorts",
          "depth": 1,
          "id": "sync-a-model-or-audience-into-braze-cohorts"
        }, {
          "value": "Create and use a Braze Segment from the Hightouch custom audience",
          "depth": 1,
          "id": "create-and-use-a-braze-segment-from-the-hightouch-custom-audience"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h3: "h3",
      strong: "strong",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: ["This destination is specific to the ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/partners/data_and_infrastructure_agility/cohort_import",
          children: "Braze Cohort Import"
        }), " feature, which lets partners like Hightouch manage user cohorts within Braze automatically. The Braze Cohorts feature is an excellent alternative to storing audience subscriptions into user-level attributes in Braze since it can reduce your Braze ", _jsx(_components.a, {
          href: "https://www.braze.com/docs/user_guide/onboarding_with_braze/data_points/",
          children: "data point"
        }), " consumption when segmenting audiences."]
      }), "\n", _jsx(_components.h3, {
        id: "get-braze-data-import-key",
        children: "Get Braze data import key"
      }), "\n", _jsxs(_components.p, {
        children: ["In Braze, navigate to ", _jsx(_components.strong, {
          children: "Technology Partners"
        }), " and select ", _jsx(_components.strong, {
          children: "Hightouch"
        }), ". Here, you will find your REST Endpoint and generate your Braze data import key. Once generated, you can create a new key or invalidate an existing one."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-cohorts-data-import-key.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "add-braze-cohorts-as-a-destination-in-hightouch",
        children: "Add Braze Cohorts as a destination in Hightouch"
      }), "\n", _jsxs(_components.p, {
        children: ["Navigate to the ", _jsx(_components.strong, {
          children: "Destination"
        }), " page in your Hightouch workspace, search for ", _jsx(_components.strong, {
          children: "Braze Cohorts"
        }), ", and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". From there, take your REST endpoint and data import key and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-cohorts-create-braze-cohort-destination.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "sync-a-model-or-audience-into-braze-cohorts",
        children: "Sync a model or audience into Braze Cohorts"
      }), "\n", _jsxs(_components.p, {
        children: ["In Hightouch, using your created ", _jsx(_components.a, {
          href: "/getting-started/create-your-first-sync#create-a-model",
          children: "model"
        }), " or ", _jsx(_components.a, {
          href: "/customer-studio/usage",
          children: "audience"
        }), ", create a new sync. Next, select the Braze Cohorts destination you created in the previous step. Lastly, in the Braze Cohorts destination configuration, select the identifier you want to match against and decide whether or not you want Hightouch to create a new Braze Cohort or update an existing one."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-cohorts-record-matching.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "create-and-use-a-braze-segment-from-the-hightouch-custom-audience",
        children: "Create and use a Braze Segment from the Hightouch custom audience"
      }), "\n", _jsxs(_components.p, {
        children: ["In Braze, navigate to ", _jsx(_components.strong, {
          children: "Segments"
        }), ", create a new segment, and select ", _jsx(_components.strong, {
          children: "Hightouch Cohorts"
        }), " as your filter. From here, you can choose which Hightouch cohort you wish to include. Once created, you can select your Hightouch cohort segment as an audience filter when creating a campaign or Canvas."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-cohorts-create-braze-segment.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "To use your Hightouch segment, create a Braze campaign or Canvas and select the segment as your target audience."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-braze-cohorts-hightouch-segment.png",
          alt: ""
        })
      })]
    });
  }
}
export default MDXContent;
